/**
 * PrologueScene — 序章《一封家书》at the pier edge of 绿色码头 (owner: prologue-builder).
 *
 * 江子潮 sits at the edge of the pier writing to 琼 while 赵呈博 watches from the sand. The letter is
 * revealed paragraph by paragraph (LetterPanel) through one non-skippable timeline per story flag;
 * each memory section launches a PrologueStageScene (MemoryStage) and resumes; the sealing and the
 * prophecy are nested skippable cinematics. Resumes from any owned flag (letter pre-filled, remaining
 * sections played) and hands off with ctx.enterRoute() after PROPHECY_REVEAL.
 *
 * Input: confirm advances dialogue / letter chunks; X / Esc / touch 取消 completes a letter chunk or
 * skips the running (nested) cinematic; `__nantian.skipCinematic()` does the same.
 *
 * Verification: `window.__nantianPrologue` (installed while the scene lives) exposes probes and the
 * `converge(flag, maxMs)` driver used by tools/verify/route.json — X/confirm pressed until the flag.
 */
import Phaser from 'phaser';
import { Depth, GAME_W } from '@/core/config';
import { bus, Events, type EventPayloads } from '@/core/events';
import { SeededRng } from '@/core/rng';
import { SceneKeys } from '@/core/scenes/keys';
import type { IActor, NpcScheduleStep } from '@/core/types/contracts';
import { WorldScene, type WorldSceneConfig } from '@/world';
import { CinematicPlayer, MEMORY_STAGES, createSceneCinematicHost, runMemoryStage, stateFromContext, type CinematicHost, type CinematicResult } from '@/cinematics';
import { DialogueRunner, hooksFromContext } from '@/dialogue';
import { runBattle } from '@/combat';
import { audio } from '@/audio';
import { getDialogueBox, getUi, uiRootOf } from '@/ui';
import { STORY_FLAGS } from '@/story/flags';
import { HARBOR_EDGE_MAP } from './maps/harborEdge';
import { ensureHarborDevTileset } from './maps/devTiles';
import { registerPrologueContent } from './register';
import { registerPrologueShowcase } from './showcase';
import { PrologueEffects } from './effects/PrologueEffects';
import { LetterPresenter } from './ui/LetterPresenter';
import { beatsWrittenAt, sectionsAfter } from './letter';
import { HARBOR_POS, sectionTimelineId } from './cinematics/data';
import { PROLOGUE_LETTER_SENT, PROLOGUE_PROPHECY } from './cinematics/harbor';
import { STAGES, isStageId } from './stages/defs';
import type { PrologueStageScene } from './stages/PrologueStageScene';

// `?showcase=prologue` — registered when this module loads (main.ts imports the scene).
registerPrologueShowcase();

/** Debug / verification handle (tools/verify/route.json, src/content/prologue/verify). */
export interface PrologueDebugHandle {
  ready: boolean;
  /** Story flag currently set (null before TITLE). */
  flag(): string | null;
  /** Timeline id the harbor player is running (intro / section wrapper), or null. */
  playing(): string | null;
  /** Nested skippable cinematic (sealing / prophecy) currently running, or null. */
  nested(): string | null;
  /** Active memory stage (null when none, or while the tutorial battle covers it). */
  stage(): { key: string; phase: string } | null;
  battle(): boolean;
  letterWaiting(): boolean;
  letterOpen(): boolean;
  dialogue(): boolean;
  /** Texture key each harbor actor resolved to (real cast sheet vs dev atlas / placeholder). */
  sheets(): Record<string, string>;
  /** World position + camera scroll of an actor (for on-screen checks). */
  actorAt(id: string): { x: number; y: number; scrollX: number; scrollY: number } | null;
  /** True while something scripted owns the input (letter typing/waiting, dialogue, cinematic, stage, battle). */
  busy(): boolean;
  /** What one confirm press does right now (letter chunk / dialogue line / stage confirm / battle confirm). */
  confirm(): void;
  /** What one X press does right now; true when a cinematic, stage or dialogue converged. */
  cancel(): boolean;
  /** Presses X (when something skippable runs) or confirm every ~100 ms until `flag` is set; resolves false on timeout. */
  converge(flag: string, maxMs?: number): Promise<boolean>;
}
declare global { interface Window { __nantianPrologue?: PrologueDebugHandle } }

const PROLOGUE_CONFIG: WorldSceneConfig = {
  player: { id: 'jiang', actor: 'jiang-zichao' },
  followers: [],
  defaultSpawn: 'letter-desk',
  playerSpeed: 72,
  weatherTransitionMs: 1500,
};

export class PrologueScene extends WorldScene {
  protected readonly mapId = HARBOR_EDGE_MAP.id;
  private dialogue: DialogueRunner | null = null;
  private cinematic: CinematicPlayer | null = null;
  private host: CinematicHost | null = null;
  private fx: PrologueEffects | null = null;
  private letter: LetterPresenter | null = null;
  private nested: CinematicPlayer | null = null;
  /** The letter lying beside 江子潮 on the pier (hidden once it goes into the bottle). */
  private paper: Phaser.GameObjects.Graphics | null = null;
  private dead = false;
  private ambientToken = 0;
  private readonly busOff: (() => void)[] = [];
  private readonly pendingTimers = new Set<() => void>();
  private readonly convergeStops = new Set<() => void>();

  constructor() { super(SceneKeys.PROLOGUE); }

  protected override worldConfig(): WorldSceneConfig { return PROLOGUE_CONFIG; }

  protected override onPreCreate(): void {
    registerPrologueContent();
    ensureHarborDevTileset(this);
  }

  protected override onCreate(): void {
    const ctx = this.ctx;
    if (!ctx) { console.warn('[prologue] no game context'); return; }
    this.dead = false;
    this.setInputLocked(true); // the harbor edge is fully scripted
    this.dressHarbor();

    this.dialogue = new DialogueRunner(getDialogueBox(this), hooksFromContext(ctx), {
      host: {
        face: (id, facing) => this.actors.get(id)?.setFacing(facing),
        emote: (id, emote) => void this.actors.get(id)?.showEmote(emote),
        sfx: (key) => { try { audio().sfx(key); } catch { /* silent */ } },
      },
    });
    this.fx = new PrologueEffects(this, { actors: () => this.actors, seed: SeededRng.hash(`${ctx.seed}:fx:harbor`), playTimeline: (id) => this.playNested(id) });
    this.letter = new LetterPresenter(this);
    const host = createSceneCinematicHost(this, {
      dialogue: this.dialogue,
      actors: this.actors,
      effects: this.fx,
      letter: (paragraph, opts) => this.letter?.reveal(paragraph, opts) ?? Promise.resolve(),
      memory: (stage, opts) => this.launchMemory(stage, opts),
      battle: (config) => runBattle(this, config),
    });
    host.setInputLocked = () => { /* stays locked for the whole prologue */ };
    this.host = host;
    this.cinematic = new CinematicPlayer(host);

    const onConfirm = (p: EventPayloads[typeof Events.INPUT_CONFIRM]): void => this.onConfirmInput(p);
    const onEnter = (p: EventPayloads[typeof Events.SCENE_ENTER]): void => { if (p.key !== SceneKeys.PROLOGUE && !this.dead) { this.dead = true; this.scene.stop(); } };
    bus.onTyped(Events.INPUT_CONFIRM, onConfirm);
    bus.onTyped(Events.SCENE_ENTER, onEnter);
    this.busOff.push(() => bus.offTyped(Events.INPUT_CONFIRM, onConfirm), () => bus.offTyped(Events.SCENE_ENTER, onEnter));

    // Verification handle: hangs off the core debug API (src/core/debug.ts installs `__nantian` for every
    // build; tools/verify/route.json + smoke.mjs drive the production preview through it as well).
    if (window.__nantian) this.installDebugHandle();
    this.startAmbient();
    void this.runStory();
  }

  protected override onShutdown(): void {
    this.dead = true;
    this.ambientToken++;
    if (window.__nantianPrologue) { window.__nantianPrologue.ready = false; delete window.__nantianPrologue; }
    for (const stop of [...this.convergeStops]) stop();
    this.convergeStops.clear();
    for (const off of this.busOff.splice(0)) { try { off(); } catch { /* ignore */ } }
    for (const done of [...this.pendingTimers]) done();
    this.pendingTimers.clear();
    try { this.dialogue?.abort(); } catch { /* ignore */ }
    try { this.letter?.destroy(); } catch { /* ignore */ }
    try { this.fx?.destroy(); } catch { /* ignore */ }
    this.dialogue = null; this.cinematic = null; this.host = null; this.fx = null; this.letter = null; this.nested = null; this.paper = null;
  }

  // ------------------------------------------------------------------ verification handle
  private activeStage(): PrologueStageScene | null {
    const s = this.scene.get(SceneKeys.MEMORY_STAGE) as PrologueStageScene | null;
    return s && s.scene.isActive() && typeof s.debugConfirm === 'function' ? s : null;
  }

  private installDebugHandle(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const battle = (): boolean => this.scene.isActive(SceneKeys.BATTLE);
    const confirm = (): void => {
      if (this.dead) return;
      const stage = this.activeStage();
      if (stage && !battle()) { stage.debugConfirm(); return; }
      bus.emitTyped(Events.INPUT_CONFIRM, { source: 'keyboard' }); // harbor letter / dialogue / battle prompt
    };
    const cancel = (): boolean => {
      if (this.dead) return false;
      const stage = this.activeStage();
      if (stage && !battle()) return stage.debugCancel();
      const skipped = ctx.skipCinematic();
      bus.emitTyped(Events.INPUT_CANCEL, { source: 'keyboard' }); // completes/continues a waiting letter chunk
      return skipped;
    };
    const handle: PrologueDebugHandle = {
      ready: true,
      flag: () => ctx.story.current(),
      playing: () => this.cinematic?.currentTimeline() ?? null,
      nested: () => this.nested?.currentTimeline() ?? null,
      stage: () => { const s = this.activeStage(); return s && !battle() ? { key: s.stageId, phase: s.stagePhase } : null; },
      battle,
      letterWaiting: () => this.letter?.isWaiting() ?? false,
      letterOpen: () => getUi(this)?.letterPanel.isOpen() ?? false,
      dialogue: () => this.dialogue?.isRunning() ?? false,
      sheets: () => Object.fromEntries([...this.actors].map(([id, a]) => [id, (a as { texture?: string }).texture ?? (a.sprite as { texture?: { key?: string } }).texture?.key ?? '?'])),
      actorAt: (id) => {
        const a = this.actors.get(id);
        if (!a || this.dead) return null;
        const cam = this.cameras.main;
        return { x: a.x, y: a.y, scrollX: cam.scrollX, scrollY: cam.scrollY };
      },
      busy: () => {
        if (this.dead) return false;
        const stage = this.activeStage();
        return battle() || !!stage || !!this.cinematic?.isPlaying() || !!this.nested?.isPlaying() || !!this.dialogue?.isRunning() || !!this.letter?.isWaiting();
      },
      confirm,
      cancel,
      converge: (flag, maxMs = 20000) => this.converge(flag, maxMs, confirm, cancel),
    };
    window.__nantianPrologue = handle;
  }

  /**
   * Verification driver: every ~100 ms press X when something skippable is running (cinematic, memory
   * stage) or confirm otherwise (letter chunk, dialogue line, battle prompt), until `flag` is set. The
   * tutorial battle is gameplay and is played through with confirm presses.
   */
  private converge(flag: string, maxMs: number, confirm: () => void, cancel: () => boolean): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const ctx = this.ctx;
      const t0 = performance.now();
      let timer: ReturnType<typeof setTimeout> | null = null;
      const stop = (): void => { if (timer !== null) clearTimeout(timer); timer = null; this.convergeStops.delete(stop); resolve(false); };
      this.convergeStops.add(stop);
      const done = (ok: boolean): void => { this.convergeStops.delete(stop); timer = null; resolve(ok); };
      const tick = (): void => {
        timer = null;
        if (!ctx || (ctx.story.snapshot().flags as string[]).includes(flag)) { done(true); return; }
        if (this.dead) { done(false); return; }
        if (performance.now() - t0 > maxMs) { done(false); return; }
        try {
          if (this.scene.isActive(SceneKeys.BATTLE)) confirm();
          else if (!cancel()) confirm();
        } catch (e) { console.warn('[prologue] converge step failed', e); }
        timer = setTimeout(tick, 100);
      };
      tick();
    });
  }

  // ------------------------------------------------------------------ story flow
  private async runStory(): Promise<void> {
    const ctx = this.ctx;
    const cinematic = this.cinematic;
    const letter = this.letter;
    if (!ctx || !cinematic || !letter) return;
    const cur = ctx.story.current();
    const fresh = !cur || cur === 'TITLE';
    const player = this.player;
    const desk = HARBOR_POS.desk;
    try {
      if (fresh) {
        player.setPosition(HARBOR_POS.gangway.x, HARBOR_POS.gangway.y + 30, 'down');
        await cinematic.play('prologue-intro');
      } else if (STORY_FLAGS.indexOf(cur) >= STORY_FLAGS.indexOf('LETTER_SENT')) {
        // The letter is already in the bottle: 江子潮 stands where he handed it over, the returner has boarded.
        player.setPosition(HARBOR_POS.handover.x, HARBOR_POS.handover.y, 'up');
        player.play('idle');
        this.actors.get('returner')?.sprite.setAlpha(0);
        this.paper?.setVisible(false);
        await this.cameraPanTo(desk.x + 40, 200, 0);
      } else {
        player.setPosition(desk.x, desk.y, 'up');
        player.play('write');
        await this.cameraPanTo(desk.x + 40, 200, 0);
      }
      if (this.dead) return;
      const sections = sectionsAfter(cur);
      const needsPanel = sections.some((s) => s.flag !== 'PROPHECY_REVEAL');
      if (!fresh && needsPanel) await letter.prefill(beatsWrittenAt(cur));
      for (const section of sections) {
        if (this.dead) return;
        await cinematic.play(sectionTimelineId(section.flag));
        if (this.dead) return;
        if (!ctx.story.has(section.flag)) ctx.story.advance(section.flag); // never soft-lock on a missing timeline
      }
      if (this.dead) return;
      await ctx.enterRoute();
    } catch (e) {
      console.error('[prologue] story flow failed', e);
      if (!this.dead) await ctx.enterRoute();
    }
  }

  /**
   * Nested skippable cinematics (sealing / hand-over, the prophecy) are staged on the pier — under the
   * letter panel's rect — so the panel is closed first; the last page has already been confirmed by the
   * reader (every letter chunk waits for a confirm). The paper prop goes into the bottle with the letter.
   */
  private async playNested(id: string): Promise<CinematicResult> {
    if (this.dead || !this.host) return 'completed';
    await this.letter?.close();
    if (this.dead || !this.host) return 'completed';
    if (id === PROLOGUE_LETTER_SENT.id) this.paper?.setVisible(false);
    if (id === PROLOGUE_PROPHECY.id) this.ambientToken++; // dusk: the unloading stops, the pier goes quiet for the monologue
    const player = new CinematicPlayer(this.host);
    this.nested = player;
    try { return await player.play(id); }
    finally { if (this.nested === player) this.nested = null; }
  }

  private async launchMemory(stage: string, opts: { fastForward: boolean }): Promise<CinematicResult> {
    const ctx = this.ctx;
    if (!ctx || !isStageId(stage)) { console.warn(`[prologue] unknown memory stage "${stage}"`); return 'completed'; }
    // A fast-forwarded section never launches the stage: stage timelines carry no route-gating state.
    if (opts.fastForward || this.dead) return 'skipped';
    const timeline = MEMORY_STAGES[stage]?.timeline ?? STAGES[stage].intro;
    const letter = this.letter;
    await letter?.close();
    await this.uiFade(true, 400);
    this.ambientToken++;
    const result = await runMemoryStage(this, stage, timeline, {
      state: stateFromContext(ctx),
      dialogueBox: getDialogueBox(this),
      letter: (paragraph, o) => this.letter?.reveal(paragraph, o) ?? Promise.resolve(),
      pauseCaller: true,
    });
    if (this.dead) return result;
    try {
      const a = audio();
      a.playMusic(this.map.music ?? null, 800);
      a.setAmbience((this.map.ambience ?? []).map((key) => ({ key, volume: 1 })), 800);
    } catch { /* silent */ }
    this.startAmbient();
    await letter?.reopen();
    await this.uiFade(false, 400);
    return result;
  }

  private uiFade(out: boolean, ms: number): Promise<void> {
    const ui = this.scene.get(SceneKeys.UI) as (Phaser.Scene & { fade?: (out: boolean, ms: number) => Promise<void> }) | null;
    if (ui?.fade && ui.scene.isActive()) return ui.fade(out, ms);
    return Promise.resolve();
  }

  private onConfirmInput(p: EventPayloads[typeof Events.INPUT_CONFIRM]): void {
    void p;
    if (this.dead || this.scene.isPaused() || uiRootOf(this)?.hasModal()) return;
    if (this.dialogue?.isRunning()) this.dialogue.advance();
  }

  // ------------------------------------------------------------------ ambience (sailors unloading)
  private startAmbient(): void {
    const token = ++this.ambientToken;
    for (const npc of this.map.npcs) {
      const actor = this.actors.get(npc.id);
      if (actor && npc.schedule && npc.schedule.length) void this.ambientLoop(actor, npc.schedule, token);
    }
  }

  private async ambientLoop(actor: IActor, steps: NpcScheduleStep[], token: number): Promise<void> {
    let i = 0;
    let guard = 0;
    while (this.ambientToken === token && !this.dead && guard++ < 10000) {
      const s = steps[i];
      if (!s) break;
      switch (s.kind) {
        case 'walk': if (s.x !== undefined && s.y !== undefined) await actor.walkTo(s.x, s.y, 58); break;
        case 'wait': await this.wait(s.ms ?? 500); break;
        case 'face': if (s.facing) actor.setFacing(s.facing); break;
        case 'emote': if (s.emote) await actor.showEmote(s.emote, s.ms); break;
        case 'loop': i = -1; break;
      }
      i++;
    }
  }

  private wait(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      if (this.dead || ms <= 0) { resolve(); return; }
      const done = (): void => { this.pendingTimers.delete(done); resolve(); };
      this.pendingTimers.add(done);
      this.time.delayedCall(ms, done);
    });
  }

  // ------------------------------------------------------------------ dev dressing
  private dressHarbor(): void {
    // mast + furled sail over the moored hull (tiles 11..17 × 1..3)
    const mast = this.add.graphics({ x: 14 * 32 + 16, y: 3 * 32 + 30 }).setDepth(Depth.WORLD_BASE + 3 * 32 + 30);
    mast.fillStyle(0x241810, 1); mast.fillRect(-3, -120, 6, 120);
    mast.fillStyle(0x8a5a32, 1); mast.fillRect(-1, -120, 1, 120);
    mast.fillStyle(0xc9bfa6, 1); mast.fillRect(-30, -104, 60, 8);
    mast.fillStyle(0x3a2a1e, 1); mast.fillRect(-34, -106, 68, 2);
    // lantern glow on the hull (map light)
    const light = this.map.lights?.[0];
    if (light) {
      const glow = this.add.graphics().setDepth(Depth.WEATHER - 1).setBlendMode(Phaser.BlendModes.ADD);
      glow.fillStyle(0xffb347, 0.10); glow.fillEllipse(light.x, light.y, light.radius * 1.3, light.radius * 0.8);
      glow.fillStyle(0xffb347, 0.16); glow.fillEllipse(light.x, light.y, light.radius * 0.7, light.radius * 0.45);
      const lamp = this.add.graphics({ x: light.x, y: light.y }).setDepth(Depth.WORLD_BASE + light.y);
      lamp.fillStyle(0x3a2a1e, 1); lamp.fillRect(-3, -10, 6, 10); lamp.fillStyle(0xfff1c0, 1); lamp.fillRect(-2, -8, 4, 5);
    }
    // the letter on the pier edge, beside 江子潮
    const paper = this.add.graphics({ x: HARBOR_POS.desk.x + 12, y: HARBOR_POS.desk.y - 2 }).setDepth(Depth.GROUND_DETAIL + 2);
    paper.fillStyle(0xf6ecd6, 1); paper.fillRect(-7, -9, 14, 10);
    paper.fillStyle(0x5a4636, 1); for (let i = 0; i < 3; i++) paper.fillRect(-5, -7 + i * 3, 10 - i * 2, 1);
    this.paper = paper;
    // cargo being unloaded
    for (const [tx, ty] of [[3, 5], [8, 5], [15, 7]] as const) {
      const c = this.add.graphics({ x: tx * 32 + 16, y: ty * 32 + 30 }).setDepth(Depth.WORLD_BASE + ty * 32 + 30);
      c.fillStyle(0xa8743a, 1); c.fillRect(-10, -22, 20, 20);
      c.fillStyle(0xd9a45a, 1); c.fillRect(-10, -22, 20, 1); c.fillRect(-10, -22, 1, 20);
      c.fillStyle(0x5a3a1e, 1); c.fillRect(-10, -3, 20, 1); c.fillRect(9, -22, 1, 20);
      for (let i = 0; i < 16; i++) { c.fillRect(-8 + i, -20 + i, 1, 1); c.fillRect(7 - i, -20 + i, 1, 1); }
    }
    // distant haze on the water
    const haze = this.add.graphics().setDepth(Depth.GROUND_DETAIL + 1);
    haze.fillStyle(0xe8f0f2, 0.22); haze.fillRect(0, 0, GAME_W, 26);
    haze.fillStyle(0xe8f0f2, 0.12); haze.fillRect(0, 26, GAME_W, 14);
  }
}
